'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import {
  Mail, MessageCircle, Clock, AlertTriangle,
  Sparkles, ArrowRight, CheckCircle2, FileText,
} from 'lucide-react'
import { Slack, Wrench, ImageIcon, Link2, ListChecks, Headset } from '@/lib/icons'
import { EASE_CUSTOM } from '@/lib/animations'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.9, ease: EASE_CUSTOM },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.8, delay: i * 0.08, ease: EASE_CUSTOM },
})

const channels = [
  {
    icon: Mail,
    title: 'Email de soporte',
    value: 'soporte@innovatheratech.com',
    desc: 'Canal principal · Respuesta en menos de 24h en horario laboral.',
    href: 'mailto:soporte@innovatheratech.com',
    cta: 'Escribir email',
    primary: true,
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    value: '+34 672 724 676',
    desc: 'Para urgencias de clientes activos. Indica primero tu empresa y proyecto.',
    href: 'https://wa.me/34672724676?text=Hola%20Innovathera,%20soy%20cliente%20y%20necesito%20soporte.',
    cta: 'Abrir WhatsApp',
    primary: false,
  },
  {
    icon: Slack,
    title: 'Canal dedicado',
    value: 'Slack / Teams por proyecto',
    desc: 'Si tu proyecto incluye canal dedicado, úsalo como vía principal de coordinación.',
    href: null,
    cta: 'Configurado por proyecto',
    primary: false,
  },
]

const sla = [
  {
    icon: AlertTriangle,
    label: 'Bug crítico',
    desc: 'Sistema caído o funcionalidad principal bloqueada.',
    time: '< 4h',
    note: 'Horario laboral · Notificar también por WhatsApp',
  },
  {
    icon: Wrench,
    label: 'Bug funcional',
    desc: 'Algo no funciona como debería pero el sistema sigue operativo.',
    time: '< 24h',
    note: 'Horario laboral',
  },
  {
    icon: Sparkles,
    label: 'Mejora · consulta · feature',
    desc: 'Sugerencia de mejora, consulta sobre funcionamiento, nueva feature.',
    time: '< 48h',
    note: 'Se evalúa según roadmap del proyecto',
  },
]

const beforeContact = [
  { icon: ListChecks, text: 'Verifica que tienes acceso al panel del proyecto' },
  { icon: ImageIcon, text: 'Adjunta capturas si reportas un bug' },
  { icon: FileText, text: 'Indica qué intentaste antes' },
  { icon: Link2, text: 'URL del entorno donde ocurre (staging / producción)' },
]

export default function SoporteClient() {
  return (
    <main>

      {/* ── Hero ── */}
      <section className="pt-28 pb-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
              <Headset className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-mono tracking-[0.25em] text-blue-600 uppercase">Soporte</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
              ¿Necesitas ayuda?{' '}
              <span className="text-slate-400 font-light">Estamos aquí.</span>
            </h1>
            <p className="text-slate-500 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              Si ya eres cliente y necesitas soporte sobre algo que hemos implementado para ti,
              estos son tus canales directos. Sin tickets ni intermediarios.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Canales ── */}
      <section className="py-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-mono tracking-[0.25em] text-slate-400 uppercase mb-6">
            01 · Canales de contacto
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {channels.map((c, i) => {
              const Icon = c.icon
              return (
                <motion.div
                  key={c.title}
                  {...stagger(i)}
                  className={`glass-card rounded-2xl p-6 flex flex-col gap-4 ${c.primary ? 'ring-2 ring-blue-300/40' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${c.primary ? 'bg-blue-600 border-blue-600' : 'bg-blue-50 border-blue-100'}`}>
                      <Icon className={`w-5 h-5 ${c.primary ? 'text-white' : 'text-blue-600'}`} />
                    </div>
                    {c.primary && (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-blue-50 text-blue-600 border-blue-100 tracking-wide uppercase">
                        Principal
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-900 text-base">{c.title}</p>
                    <p className="text-slate-700 text-sm font-medium mt-1 break-all">{c.value}</p>
                    <p className="text-slate-500 text-xs mt-2 leading-relaxed">{c.desc}</p>
                  </div>
                  {c.href ? (
                    <a
                      href={c.href}
                      target={c.href.startsWith('http') ? '_blank' : undefined}
                      rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className={`inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-95 ${c.primary ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_4px_16px_rgba(37,99,235,0.3)]' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'}`}
                    >
                      {c.cta}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <div className="inline-flex items-center justify-center w-full py-2.5 rounded-xl text-xs text-slate-500 font-mono uppercase tracking-wider border border-dashed border-slate-300/60">
                      {c.cta}
                    </div>
                  )}
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── SLA ── */}
      <section className="py-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-mono tracking-[0.25em] text-slate-400 uppercase mb-6">
            02 · Tiempos de respuesta (SLA)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sla.map((s, i) => {
              const Icon = s.icon
              return (
                <motion.div
                  key={s.label}
                  {...stagger(i)}
                  className="glass-card rounded-2xl p-6 flex flex-col gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-2xl font-black tracking-tight text-slate-900">{s.time}</span>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm mb-1">{s.label}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{s.desc}</p>
                  </div>
                  <p className="text-[11px] text-slate-400 font-mono uppercase tracking-wider mt-auto pt-3 border-t border-slate-200/60">
                    {s.note}
                  </p>
                </motion.div>
              )
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE_CUSTOM }}
            className="mt-6 flex items-center justify-center gap-2 text-xs font-mono tracking-widest text-slate-400 uppercase"
          >
            <Clock className="w-3.5 h-3.5" />
            Horario laboral: L-V · 9:00 – 18:00 (CET)
          </motion.div>
        </div>
      </section>

      {/* ── Antes de contactarnos ── */}
      <section className="py-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-mono tracking-[0.25em] text-slate-400 uppercase mb-6">
            03 · Antes de contactarnos
          </div>
          <motion.div
            {...fadeUp}
            className="glass-card rounded-2xl p-6 md:p-8"
          >
            <p className="text-slate-600 mb-5 max-w-2xl">
              Para que podamos resolverlo lo más rápido posible, ayúdanos con esto:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {beforeContact.map(item => {
                const Icon = item.icon
                return (
                  <div key={item.text} className="flex items-start gap-3 p-3 rounded-xl bg-white/40 border border-slate-200/40">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                      <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
                  </div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ¿No eres cliente? ── */}
      <section className="py-12 pb-20 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            {...fadeUp}
            className="glass-card rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-black text-slate-900 text-lg">¿No eres cliente y tienes una duda?</p>
                <p className="text-slate-500 text-sm mt-1 max-w-lg">
                  Si quieres conocernos, contratar o pedir presupuesto, este no es el canal —
                  te llevamos al sitio correcto.
                </p>
              </div>
            </div>
            <Link
              href="/contacto"
              className="shrink-0 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95"
            >
              Solicitar información
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

    </main>
  )
}
